import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Banknote, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const WpsSettingsTab = ({ isViewer = false }) => {
  const [companies, setCompanies] = useState({}); // { name: {employer_id, routing_code} }
  const [names, setNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/api/settings/wps`, { headers })
      .then(r => {
        if (!mounted) return;
        const cfg = r.data?.current?.companies || {};
        const used = r.data?.visa_companies || [];
        const merged = [...new Set([...used, ...Object.keys(cfg)])].sort();
        setNames(merged);
        const seeded = {};
        merged.forEach(n => {
          seeded[n] = {
            employer_id: cfg[n]?.employer_id || '',
            routing_code: cfg[n]?.routing_code || '',
          };
        });
        setCompanies(seeded);
      })
      .catch(() => toast({ title: 'Failed to load WPS settings', variant: 'destructive' }))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (name, field, value) =>
    setCompanies(prev => ({ ...prev, [name]: { ...prev[name], [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/wps`, { companies }, { headers });
      toast({ title: 'WPS settings saved', description: 'SIF files will use these Employer IDs & routing codes.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="wps-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Banknote className="h-5 w-5 text-emerald-600" />
          WPS / SIF Settings
        </CardTitle>
        <p className="text-xs text-gray-500">
          Set the Employer (Establishment) ID and bank routing code for each visa company. These fill the SIF control record used when generating the WPS file from Payroll.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : names.length === 0 ? (
          <p className="text-sm text-gray-500">No visa companies found on active employees.</p>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="text-left p-2">Visa Company</th>
                    <th className="text-left p-2">Employer ID (Establishment)</th>
                    <th className="text-left p-2">Bank Routing Code</th>
                  </tr>
                </thead>
                <tbody>
                  {names.map(name => (
                    <tr key={name} className="border-t" data-testid={`wps-row-${name}`}>
                      <td className="p-2 font-medium text-gray-800">{name}</td>
                      <td className="p-2">
                        <Input
                          value={companies[name]?.employer_id || ''}
                          onChange={e => setField(name, 'employer_id', e.target.value)}
                          placeholder="e.g. 0000000896885"
                          disabled={isViewer}
                          className="font-mono h-9"
                          data-testid={`wps-employer-${name}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={companies[name]?.routing_code || ''}
                          onChange={e => setField(name, 'routing_code', e.target.value)}
                          placeholder="e.g. 629410101"
                          disabled={isViewer}
                          className="font-mono h-9"
                          data-testid={`wps-routing-${name}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving || isViewer} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="wps-settings-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WpsSettingsTab;
